<template>
  <div class="card shadow-sm border-0 rounded-lg">
    <div class="card-header bg-success">
      <h4 class="mb-0 fw-bold">
        {{ $t('myLogOffer.mnimTklifim') }}
      </h4>
    </div>
    <div class="card-body">
      <div
        v-for="(p,i) in getPQTPrices()"
        :key="i"
        class="mb-4"
      >
        <!-- ROW 1: MAIN PRODUCT -->
        <div class="card border-0 shadow-sm mb-3">
          <div class="card-body">
            <div class="row g-4">
              <!-- LEFT: Main Product Detail -->
              <div class="col-lg-6 col-md-12">
                <h5 class="card-title fw-bold">
                  {{ p.product.name }}
                </h5>
                <div class="badge bg-light text-dark mb-3">
                  {{ p.product.count }} {{ p.product.unit }}
                </div>

                <h6 class="fw-bold mt-3">
                  {{ $t("request.xsusiTlblr") }}
                </h6>
                <ul class="list-group list-group-flush mb-3">
                  <li 
                    v-for="(req, index) in p.reqDetail"
                    :key="`req-${index}`"
                    class="list-group-item bg-transparent px-0"
                  >
                    {{ `${index + 1}. ` }}{{ req.detail }}
                  </li>
                </ul>

                <!-- Main Product Tech Specs Files -->
                <h6
                  v-if="p.techSpecsFiles && p.techSpecsFiles.length"
                  class="fw-bold mt-3"
                >
                  {{ $t("myPQTOffer.fayllar") }}
                </h6>
                <div class="row mb-3">
                  <div
                    v-for="(f, ii) in p.techSpecsFiles"
                    :key="ii"
                    class="col-md-4 col-sm-6 mb-2"
                  >
                    <a
                      v-if="isPhoto(f)"
                      target="_blank"
                      :href="getPhoto(f, '')"
                      class="d-block"
                    >
                      <img
                        :src="getPhoto(f, 'medium', '/images/no-image.png')"
                        class="img-thumbnail shadow-sm"
                        style="max-height: 150px; width: 100%; object-fit: cover;"
                        alt="-"
                      >
                    </a>
                    <a
                      v-else
                      target="_blank"
                      :href="getPhoto(f, '')"
                      class="card border-0 shadow-sm"
                      style="height: 100px"
                    >
                      <div
                        class="card-body d-flex text-dark w-100
                   justify-content-center align-items-center"
                      >
                        <div class="text-center">
                          <i class="fas fa-file-alt fa-2x mb-2" /><br>
                          {{ f.split('.').pop().toUpperCase() }}
                        </div>
                      </div>
                    </a>
                  </div>
                </div>

                <!-- Main Product Technical Specification -->
                <div class="mt-3">
                  <h6 class="fw-bold">
                    {{ $t("myPQTOffer.texnikiSpesifikasiya") }}
                  </h6>
                  <p class="text-muted">
                    {{ p.techSpecification || $t('myPQTOffer.noSpecification') }}
                  </p>
                </div>
              </div>

              <!-- RIGHT: Main Product Pricing -->
              <div
                class="col-lg-6 col-md-12"
                :class="{'border-success border-start': p.isSelected}"
              >
                <div class="card h-100 border-0 shadow-sm">
                  <div class="card-header bg-primary d-flex justify-content-between align-items-center">
                    <h6 class="mb-0 fw-bold">
                      <template v-if="p.rank">
                        {{ $t('myPQTOffer.rank', {rank: formatRank(p.rank)}) }}
                      </template>
                      <template v-else>
                        {{ $t('myPQTOffer.noOffer') }}
                      </template>
                    </h6>
                    <!-- Product Rank Badge -->
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-borderless">
                        <tbody>
                          <tr>
                            <td class="text-end fw-bold">
                              {{ $t('myPQTOffer.unitPrice') }}:
                            </td>
                            <td class="text-end">
                              {{ p.price }} ₼ x {{ p.product.count }} {{ p.product.unit }}
                            </td>
                          </tr>
                          <tr>
                            <td class="text-end fw-bold">
                              {{ $t('myPQTOffer.taxId') }}:
                            </td>
                            <td class="text-end">
                              {{
                                p.isVatPayer
                                  ? (p.price * p.product.count * 0.18).toFixed(2)
                                  : 0
                              }} ₼
                            </td>
                          </tr>
                          <tr class="border-top">
                            <td class="text-end fw-bold">
                              {{ $t("myPQTOffer.overAllPrice") }}:
                            </td>
                            <td class="text-end fw-bold">
                              {{
                                p.isVatPayer
                                  ? (p.price * p.product.count * 0.18 + p.price * p.product.count).toFixed(2)
                                  : (p.price * p.product.count).toFixed(2)
                              }} ₼
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div
                      v-if="p.isSelected"
                      class="alert alert-success mt-2 mb-0 text-center"
                    >
                      <i class="fas fa-check-circle me-2" />
                      <strong>{{ $t('myPQTOffer.selectedOffer') }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ROW 2: ALTERNATIVE PRODUCT (shown only if it exists) -->
            <div
              v-if="p.alternativeOffer"
              class="card border-0 shadow-sm mb-3 border-primary border-top"
            >
              <div class="card-header bg-light">
                <h5 class="mb-0 text-primary">
                  <i class="fas fa-exchange-alt me-2" />
                  {{ $t('request.alternativTklif') }}
                </h5>
              </div>
              <div class="card-body">
                <div class="row g-4">
                  <!-- LEFT: Alternative Product Detail -->
                  <div
                    class="col-lg-6 col-md-12"
                    :class="{'border-success border-start': p.alternativeOffer.isSelected}"
                  >
                    <h5 class="card-title fw-bold text-primary">
                      {{ p.alternativeOffer.productName }}
                    </h5>
                    <div class="badge bg-light text-primary mb-3">
                      {{ p.product.count }} {{ p.product.unit }}
                    </div>

                    <!-- Alternative Product Tech Specs Files -->
                    <h6
                      v-if="p.techSpecsFilesAlternative && p.techSpecsFilesAlternative.length"
                      class="fw-bold mt-3 text-primary"
                    >
                      {{ $t("myPQTOffer.fayllar") }}
                    </h6>
                    <div class="row mb-3">
                      <div
                        v-for="(f, ii) in p.techSpecsFilesAlternative"
                        :key="ii"
                        class="col-md-4 col-sm-6 mb-2"
                      >
                        <a
                          v-if="isPhoto(f)"
                          target="_blank"
                          :href="getPhoto(f, '')"
                          class="d-block"
                        >
                          <img
                            :src="getPhoto(f, 'medium', '/images/no-image.png')"
                            class="img-thumbnail shadow-sm"
                            style="max-height: 150px; width: 100%; object-fit: cover;"
                            alt="-"
                          >
                        </a>
                        <a
                          v-else
                          target="_blank"
                          :href="getPhoto(f, '')"
                          class="card border-0 shadow-sm"
                          style="height: 100px"
                        >
                          <div
                            class="card-body d-flex text-primary w-100
                       justify-content-center align-items-center"
                          >
                            <div class="text-center">
                              <i class="fas fa-file-alt fa-2x mb-2" /><br>
                              {{ f.split('.').pop().toUpperCase() }}
                            </div>
                          </div>
                        </a>
                      </div>
                    </div>

                    <!-- Alternative Product Technical Specification -->
                    <div class="mt-3">
                      <h6 class="fw-bold text-primary">
                        {{ $t("myPQTOffer.texnikiSpesifikasiya") }}
                      </h6>
                      <p class="text-primary">
                        {{ p.alternativeOffer.techSpecs || $t('myPQTOffer.noSpecification') }}
                      </p>
                    </div>
                  </div>

                  <!-- RIGHT: Alternative Product Pricing -->
                  <div
                    class="col-lg-6 col-md-12"
                    :class="{'border-success border-start': p.alternativeOffer.isSelected}"
                  >
                    <div class="card h-100 border-0 shadow-sm">
                      <div class="card-header bg-light d-flex justify-content-between align-items-center">
                        <h6 class="mb-0 fw-bold text-primary">
                          <template v-if="p.alternativeOffer.rank">
                            {{ $t('myPQTOffer.rank', {rank: formatRank(p.alternativeOffer.rank)}) }}
                          </template>
                          <template v-else>
                            {{ $t('myPQTOffer.noOffer') }}
                          </template>
                        </h6>
                      </div>
                      <div class="card-body">
                        <div class="table-responsive">
                          <table class="table table-borderless">
                            <tbody>
                              <tr>
                                <td class="text-end fw-bold text-primary">
                                  {{ $t('myPQTOffer.unitPrice') }}:
                                </td>
                                <td class="text-end text-primary">
                                  {{ p.alternativeOffer.price }} ₼ x {{ p.product.count }} {{ p.product.unit }}
                                </td>
                              </tr>
                              <tr>
                                <td class="text-end fw-bold text-primary">
                                  {{ $t('myPQTOffer.taxId') }}:
                                </td>
                                <td class="text-end text-primary">
                                  {{
                                    p.alternativeOffer.isVatPayer
                                      ? (p.alternativeOffer.price * p.product.count * 0.18).toFixed(2)
                                      : 0
                                  }} ₼
                                </td>
                              </tr>
                              <tr class="border-top">
                                <td class="text-end fw-bold text-primary">
                                  {{ $t("myPQTOffer.overAllPrice") }}:
                                </td>
                                <td class="text-end fw-bold text-primary">
                                  {{
                                    p.alternativeOffer.isVatPayer
                                      ? (p.alternativeOffer.price * p.product.count * 0.18
                                        + p.alternativeOffer.price * p.product.count).toFixed(2)
                                      : (p.alternativeOffer.price * p.product.count).toFixed(2)
                                  }} ₼
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        <div
                          v-if="p.alternativeOffer.isSelected"
                          class="alert alert-success mt-2 mb-0 text-center"
                        >
                          <i class="fas fa-check-circle me-2" />
                          <strong>{{ $t('myPQTOffer.selectedOffer') }}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <hr>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="myPQTOffer"
        class="card border-0 shadow-sm mb-4"
      >
        <div class="card-header bg-light">
          <h5
            class="mb-0 fw-bold "
            style="color: black"
          >
            {{ $t('myPQTOffer.offerDetails') }}
          </h5>
        </div>
        <div class="card-body">
          <div class="row g-4">
            <div class="col-lg-6 col-md-12">
              <ul class="list-group list-group-flush">
                <li class="list-group-item d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center px-0 gap-2">
                  <span class="fw-bold">{{ $t('myPQTOffer.tklifinEtibarllqTarixi') }}</span>
                  <span class="badge bg-light text-dark">
                    {{ myPQTOffer.valid.trim() === '' ? $t('myPQTOffer.noDate') : getMoment(myPQTOffer.valid) }}
                  </span>
                </li>
                <li class="list-group-item d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center px-0 gap-2">
                  <span class="fw-bold">{{ $t('detailedComparisonModal.deliveryOption') }}</span>
                  <span class="badge bg-light text-dark">
                    {{ (myPQTOffer.deliveryDays && myPQTOffer.deliveryDays !== 0) ? myPQTOffer.deliveryDays + $t('myPQTOffer.day') : '' }}
                    {{ myPQTOffer.incoterms ? myPQTOffer.incoterms : '' }}
                    {{ myPQTOffer.incoterms === 'DDP' ? requestExtras.deliveryAddress : '' }}
                    {{ (!myPQTOffer.deliveryDays || myPQTOffer.deliveryDays === 0) && !myPQTOffer.incoterms ? $t('myPQTOffer.noSpecification') : '' }}
                  </span>
                </li>
                <li class="list-group-item d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center px-0 gap-2">
                  <span class="fw-bold">{{ $t('detailedComparisonModal.manufacturerCountry') }}</span>
                  <span class="badge bg-light text-dark">{{ myPQTOffer.manufacturerCountry }}</span>
                </li>
              </ul>
            </div>
            <div class="col-lg-6 col-md-12">
              <ul class="list-group list-group-flush">
                <li class="list-group-item d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center px-0 gap-2">
                  <span class="fw-bold">{{ $t('detailedComparisonModal.paymentOption') }}</span>
                  <span class="badge bg-light text-dark">
                    {{ getPaymentOptionLabel(myPQTOffer.paymentOption) }}
                    {{ myPQTOffer.paymentComment !== undefined ? myPQTOffer.paymentComment : '' }}
                  </span>
                </li>
                <li class="list-group-item d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center px-0 gap-2">
                  <span class="fw-bold">{{ $t('detailedComparisonModal.warrantyPeriod') }}</span>
                  <span class="badge bg-light text-dark">
                    {{ myPQTOffer.warrantyPeriod + $t('myPQTOffer.month') }}
                    {{ myPQTOffer.warrantyComment !== undefined ? myPQTOffer.warrantyComment : '' }}
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div
            v-if="myPQTOffer.files"
            class="mt-4"
          >
            <h6 class="fw-bold mb-3">
              {{ $t('myPQTOffer.fayllar') }}
            </h6>
            <div class="row">
              <div
                v-for="(f,i) in myPQTOffer.files.split(',')"
                :key="i"
                class="col-md-3 col-sm-6 mb-3"
              >
                <a
                  v-if="isPhoto(f)"
                  target="_blank"
                  :href="getPhoto(f,'')"
                  class="d-block"
                >
                  <img
                    :src="getPhoto(f,'medium','/images/no-image.png')"
                    class="img-thumbnail shadow-sm"
                    style="max-height: 150px; width: 100%; object-fit: cover;"
                    alt="-"
                  >
                </a>
                <a
                  v-else
                  target="_blank"
                  :href="getPhoto(f,'')"
                  class="card border-0 shadow-sm"
                  style="height: 100px"
                >
                  <div class="card-body d-flex text-dark w-100 justify-content-center align-items-center">
                    <div class="text-center">
                      <i class="fas fa-file-alt fa-2x mb-2" /><br>
                      {{ f.split('.').pop().toUpperCase() }}
                    </div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="card-footer bg-light">
        <div class="d-flex flex-wrap justify-content-between align-items-center">
          <div class="mb-2 mb-md-0">
            <span class="badge bg-warning p-2 fs-6">
              <i class="fas fa-tag me-2" />
              {{ $t('myPQTOffer.toplamTklif') }} {{ $t('myPQTOffer.azn', {totalPrice: mainTotal.toFixed(2)}) }}
            </span>
          </div>

          <div v-if="alternativeTotal > 0">
            <span class="badge bg-success p-2 fs-6">
              <i class="fas fa-exchange-alt me-2" />
              {{ $t('myPQTOffer.alternativeTotalOffer') }}
              {{ $t('myPQTOffer.azn', {totalPrice: alternativeTotal.toFixed(2)}) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import {getPhoto, getAzOrdinalSuffix} from "@/repositories/Utils";
import moment from "moment";
import {getPaymentOptionLabel} from "@/repositories/GeneralDataRepository";

export default {
  name: 'MyPQTOffer',
  props: {
    products: {},
    offers: {},
    request: {}
  },

  computed: {
    mainTotal() {
      // Sum of main prices across all items in the first offer
      return this.getPQTPrices().reduce((acc, item) => {
        const base = item.price * item.product.count;
        // If item is VAT payer, multiply by 1.18
        return acc + (item.isVatPayer ? base * 1.18 : base);
      }, 0);
    },
    alternativeTotal() {
      const prices = this.getPQTPrices();
      // Check if any object has an alternativeOffer
      if (!prices.some(item => item.alternativeOffer)) {
        return 0;
      }
      // Sum of alternative prices (if any) across all items
      return this.getPQTPrices().reduce((acc, item) => {
        // Only add if there's an alternativeOffer
        if (item.alternativeOffer) {
          const base = item.alternativeOffer.price * item.product.count;
          return acc + (item.alternativeOffer.isVatPayer ? base * 1.18 : base);
        } else {
          const base = item.price * item.product.count;
          return acc + (item.isVatPayer ? base * 1.18 : base);
        }

      }, 0);
    },
    myPQTOffer() {
      if (this.offers.length > 0) {
        return JSON.parse(this.offers[0].prices)['extras']
      }
      return null;
    },
    requirements() {
      if (this.offers.length > 0) {
        return JSON.parse(this.offers[0].prices)['prices']
      }
      return null;
    },
    requestExtras() {
      return JSON.parse(this.request.extras)
    }
  },


  methods: {
    getPaymentOptionLabel,
    getMoment(time) {
      moment.locale(this.$i18n.locale);
      return moment(new Date(time)).format('DD MMM YYYY HH:mm')
    },
    isPhoto(path) {
      return ['jpg', 'png', 'gif', 'jpeg'].indexOf(path.toLowerCase().split('.').pop()) > -1;
    },
    getPhoto(path, size, defaultPhoto) {
      return getPhoto(path, size, defaultPhoto);
    },
    formatRank(rank) {
      if (!rank || rank === 'N/A') return 'N/A';
      // Only use the dynamic suffix if the current locale is Azerbaijani
      if (this.$i18n.locale === 'az') {
        return rank + getAzOrdinalSuffix(rank);
      }

      // For other languages, just return the rank as is
      return rank;
    },
    getPQTPrices() {

      let prices = [];

      let json = JSON.parse(this.offers[0].prices);
      if (!json.prices) return [];
      json.prices.forEach((k) => {
        prices.push({
          product: this.getProductById(k.product),
          price: k.price,
          isVatPayer: k.isVatPayer === 'true',
          reqDetail: k.reqDetails,
          rank:k.rank,
          techSpecification: k.techSpecification,
          techSpecsFiles: k.techSpecsFiles,
          isSelected: k.isSelected,
          techSpecsFilesAlternative: k.techSpecsFilesAlternative,
          alternativeOffer: k.alternativeOffer
        })
      })
      return prices;

    },

    getProductById(id) {

      let prod;
      this.products.forEach(p => {
        if (p.id === parseInt(id)) {
          prod = p;
        }
      })

      return prod;

    },
  }
}
</script>
